import {GUID} from "@utils/AppConstants";
import {useEffect, useMemo, useRef, useState} from "react";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import axios from "axios";
import {
    OrderRecipeComponentEditModel, OrderRecipeComponentSaveRequest, OrderRecipeEditModel,
    OrderRecipeEditModuleModel, OrderRecipePreviewModel,
    OrderRecipeSaveRequest, OrderRecipeVersionModel,
} from "@pages/orders/order.models";
import {ApiUrl_Orders_GetOrderRecipe, ApiUrl_Orders_SaveOrderRecipe, ApiUrl_OrderRecipe_GetVersion} from "@utils/Endpoints";
import {ApiResponseDto, NameWithId} from "@utils/Models";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import FlintFieldset from "@components/layout/FlintFieldset";
import {useTranslation} from "react-i18next";
import FlintTextBox from "@components/controls/FlintTextBox";
import FlintTextArea from "@components/controls/FlintTextArea";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import SapCodeBadge from "@components/page_components/sap_code/SapCodeBadge";
import SapCodeEditPopup from "@components/page_components/sap_code/SapCodeEditPopup";
import OrderRecipeComponentEdit from "@pages/orders/preview/recipes/edit/OrderRecipeComponentEdit";
import FlintButton_Add from "@components/controls/buttons/FlintButton_Add";
import {CommonUtils} from "@utils/CommonUtils";
import NewGuid = CommonUtils.NewGuid;
import FlintButton_Save from "@components/controls/buttons/FlintButton_Save";
import * as Yup from "yup"
import useVivoValidation from "@utils/validation/useVivoValidation";
import {Switch} from "@progress/kendo-react-inputs";
import FlintButton_Cancel from "@components/controls/buttons/FlintButton_Cancel";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboardList, faFlask, faInfoCircle, faWandMagicSparkles} from "@fortawesome/free-solid-svg-icons";
import FlintButton from "@components/controls/FlintButton";
import {useVivoPopupContext} from "@utils/popups/VivoPopupContext";
import {useFlintDialogContext} from "@components/page_components/common/dialogs/FlintDialogContextProvider";
import ColourTypeDropdownList from "@pages/orders/preview/recipes/edit/ColourTypeDropdownList";
import RecipeVersionPager from "@pages/orders/preview/recipes/edit/RecipeVersionPager";
import OrderRecipeLineagePanel from "@pages/orders/preview/recipes/edit/lineage/OrderRecipeLineagePanel";
import styles from "./OrderRecipeEditPanel.module.scss";

export default function OrderRecipeEditPanel(props: OrderRecipeEditComponentProps) {
    const {state, utils, handlers} = useOrderRecipeEditComponent(props);
    const {t} = useTranslation();

    function changeTypeBadge(changeType: string) {
        if (changeType === "Deleted") return <span className="badge bg-danger">{t("ORDER_RECIPE_HISTORY_ChangeType_Deleted")}</span>;
        if (changeType === "Created") return <span className="badge bg-success">{t("ORDER_RECIPE_HISTORY_ChangeType_Created")}</span>;
        if (changeType === "Updated") return <span className="badge bg-secondary">{t("ORDER_RECIPE_HISTORY_ChangeType_Updated")}</span>;
        return <span className="badge bg-secondary">{changeType}</span>;
    }

    const versionDetail = state.viewingVersionDetail.get;

    return (
        <div className={"dialog-content-wrapper"}>
            <FlintFieldset marginBottom={0}>
                <FlintLoadingWrapper showLoading={state.isLoading.get || state.isLoadingVersion.get}>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <div className={styles.sectionHeader}>
                        <FontAwesomeIcon icon={faClipboardList}/>
                        {t("ORDER_PREVIEW_RecipeEditPanelTitle")}
                        {state.recipe.get?.DetachedOnUtc && (
                            <span className="badge bg-warning" title={t("ORDER_RECIPE_LINEAGE_DetachedHint")}>
                                {t("ORDER_RECIPE_LINEAGE_StatusDetachedCopy")}
                            </span>
                        )}
                    </div>

                    <div className={"row align-items-center"}>
                        <div className={"col-md-3 form-group"}>
                            <FlintTextBox
                                maxLength={50}
                                disabled={utils.isViewOnlyMode}
                                value={utils.displayRecipe?.CodeGeneratorNumber}
                                onChange={x => handlers.handleOnRecipeUpdated("CodeGeneratorNumber", x.value)}
                                validationData={{
                                    validationMessage: utils.validators.recipeValidator.formErrors?.CodeGeneratorNumber,
                                    validateFunction: utils.validators.recipeValidator.validateForm,
                                    optionalObjectToValidate: utils.displayRecipe,
                                }}
                                label={t("ORDER_RECIPE_CodeGeneratorNumber")}/>
                            {!utils.displayRecipe?.CodeGeneratorNumber && (
                                <small className={"text-muted d-block mt-1"}>
                                    {t("ORDER_RECIPE_CodeGeneratorNumberHelperEmpty")}
                                </small>
                            )}
                        </div>
                        <div className={"col-md-3 form-group"}>
                            <FlintDropDownList<NameWithId>
                                required={true}
                                disabled={utils.isViewOnlyMode}
                                dataSource={utils.unitsOfMeasure}
                                selectedValue={utils.displayRecipe?.UnitOfMeasure}
                                onItemSelected={x => handlers.handleOnRecipeUpdated("UnitOfMeasure", x)}
                                validationData={{
                                    validationMessage: utils.validators.recipeValidator.formErrors?.["UnitOfMeasure.Id"]
                                        ?? utils.validators.recipeValidator.formErrors?.UnitOfMeasure,
                                    validateFunction: utils.validators.recipeValidator.validateForm,
                                    optionalObjectToValidate: utils.displayRecipe,
                                }}
                                label={t("COMMON_Unit")}/>
                        </div>
                        <div className={"col-md-3 form-group"}>
                            <ColourTypeDropdownList
                                required={true}
                                disabled={utils.isViewOnlyMode}
                                dataSource={utils.colourTypeList}
                                selectedValue={utils.displaySelectedColourType}
                                onItemSelected={x => handlers.handleOnRecipeUpdated("ColourTypeId", x.Id)}
                                validationData={{
                                    validationMessage: utils.validators.recipeValidator.formErrors?.["ColourTypeId"],
                                    validateFunction: utils.validators.recipeValidator.validateForm,
                                    optionalObjectToValidate: utils.displayRecipe,
                                }}
                                label={t("ORDER_RECIPE_ColourType")}/>
                        </div>
                        <div className={"col-md-3 form-group"}>
                            <SapCodeBadge
                                value={utils.displayRecipe?.SapCode}
                                isReadOnly={utils.isViewOnlyMode}
                                onSave={value => handlers.handleOnRecipeUpdated("SapCode", value)}
                                onRemove={() => handlers.handleOnRecipeUpdated("SapCode", "")}
                            />
                        </div>
                        <div className={"col-md-12 form-group"}>
                            <div className={styles.descriptionField}>
                                <div className={styles.descriptionInput}>
                                    <FlintTextBox value={utils.displayRecipe?.Description}
                                                  disabled={utils.isViewOnlyMode}
                                                  onChange={x => handlers.handleOnRecipeUpdated("Description", x.value)}
                                                  maxLength={255}
                                                  label={t("RECIPES_FormulaDescription")}/>
                                </div>
                                {!utils.isViewOnlyMode && (
                                    <FlintButton fillMode={"outline"}
                                                 themeColor={"primary"}
                                                 onClick={handlers.handleGenerateDescriptionClicked}>
                                        <FontAwesomeIcon icon={faWandMagicSparkles} className={"me-1"}/>
                                        {t("RECIPES_DescriptionGenerator_OpenButton")}
                                    </FlintButton>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.componentsSection}>
                        <div className={styles.sectionHeader}>
                            <FontAwesomeIcon icon={faFlask}/>
                            {t("RECIPES_Components")}
                            {utils.hasSeriesContext && !utils.isViewOnlyMode && (
                                <>
                                    <span className={styles.sectionHeaderDivider}/>
                                    <span className={"d-flex align-items-center gap-2 fs-6 fw-normal"}
                                          title={t("ORDER_RECIPE_SearchFullDatabase_Hint")}>
                                        <Switch checked={state.isFullSearch.get}
                                                onChange={e => handlers.handleFullSearchToggled(e.value)}
                                                size={"small"}/>
                                        {t("ORDER_RECIPE_SearchFullDatabase")}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className={"recipe-components"}>
                            {utils.displayRecipe?.Components.map((c, i) => (
                                <OrderRecipeComponentEdit key={c.Guid}
                                                          TotalAmount={utils.displayTotalAmount}
                                                          OnComponentDeleted={handlers.handleComponentDeleted}
                                                          OnComponentUpdated={handlers.handleOnComponentUpdated}
                                                          Recipe={utils.displayRecipe!}
                                                          ComponentIndex={i}
                                                          Disabled={utils.isViewOnlyMode}
                                                          ComponentPickerDisabled={utils.isLabDivisionMismatch}
                                                          SeriesId={utils.effectiveSeriesId}
                                                          IgnoreSeriesFilter={state.isFullSearch.get}
                                                          recipeValidator={utils.validators.recipeValidator}/>
                            ))}
                        </div>
                        {utils.displayTotalAmount > 0 && (
                            <div className={styles.totalRow}>
                                <span>{t("total")}:</span>
                                <span className={styles.totalValue}>
                                    {utils.displayTotalAmount.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}&nbsp;{utils.displayRecipe?.UnitOfMeasure?.Name}
                                </span>
                            </div>
                        )}
                        {utils.isLabDivisionMismatch && !utils.isViewOnlyMode && (
                            <div className={"alert alert-warning d-flex align-items-center gap-2 py-2 mt-2"}>
                                <FontAwesomeIcon icon={faInfoCircle}/>
                                {t("ORDER_RECIPES_LabDivisionMismatchWarning")}
                            </div>
                        )}
                        {!utils.isViewOnlyMode && (
                            <div className={"d-flex justify-content-center mt-2"}>
                                <FlintButton_Add fillMode={"outline"} disabled={utils.isLabDivisionMismatch}
                                                 onClick={handlers.handleAddNewComponentClicked}
                                                 buttonText={t("COMPONENTS_AddNew")}/>
                            </div>
                        )}
                    </div>

                    {props.RecipeGuid && !utils.isViewOnlyMode && (
                        <div className={"form-group mt-3"}>
                            <FlintTextArea
                                required
                                label={t("ORDER_RECIPE_ChangeReason")}
                                placeholder={t("ORDER_RECIPE_ChangeReasonPlaceholder")}
                                value={state.changeReason.get}
                                onChange={e => handlers.handleChangeReasonChanged(e.value)}
                                rows={3}
                                validationData={{validationMessage: state.changeReason.error}}
                            />
                        </div>
                    )}
                </FlintLoadingWrapper>
            </FlintFieldset>

            <OrderRecipeLineagePanel AssignmentGuid={props.AssignmentGuid}/>

            {/* ── Version banner ──────────────────────────────────── */}
            {utils.isHistoricalMode ? (
                versionDetail && (
                    <div className={styles.versionBanner}>
                        <div className={styles.versionBannerMeta}>
                            <span className="badge bg-primary">
                                {t("ORDER_RECIPE_HISTORY_Version")} {versionDetail.VersionNumber}
                            </span>
                            {changeTypeBadge(versionDetail.ChangeType)}
                            {versionDetail.ModifiedBy && (
                                <span className={styles.versionBannerLabel}>{versionDetail.ModifiedBy}</span>
                            )}
                            {versionDetail.ModifiedOnUtc && (
                                <span>{new Date(versionDetail.ModifiedOnUtc).toLocaleString('sv').slice(0, 16)}</span>
                            )}
                        </div>
                        {versionDetail.ChangeReason && (
                            <div className={styles.versionBannerReason}>
                                <span className={styles.versionBannerLabel}>{t("ORDER_RECIPE_HISTORY_ChangeReason")}:</span>
                                {versionDetail.ChangeReason}
                            </div>
                        )}
                    </div>
                )
            ) : (
                utils.hasHistory && state.recipe.get && (
                    <div className={styles.versionBanner}>
                        <div className={styles.versionBannerMeta}>
                            <span className="badge bg-primary">
                                {t("ORDER_RECIPE_HISTORY_Version")} {utils.currentRecipeVersionNumber}
                            </span>
                            {state.recipe.get.ModifiedBy && (
                                <span className={styles.versionBannerLabel}>{state.recipe.get.ModifiedBy}</span>
                            )}
                            {state.recipe.get.ModifiedOnUtc && (
                                <span>{new Date(state.recipe.get.ModifiedOnUtc).toLocaleString('sv').slice(0, 16)}</span>
                            )}
                        </div>
                        {state.recipe.get.ChangeReason && (
                            <div className={styles.versionBannerReason}>
                                <span className={styles.versionBannerLabel}>{t("ORDER_RECIPE_HISTORY_ChangeReason")}:</span>
                                {state.recipe.get.ChangeReason}
                            </div>
                        )}
                    </div>
                )
            )}

            <div className={"dialog-actions"}>
                <div className={styles.dialogFooter}>
                    <div className={styles.footerLeft}>
                        {utils.hasHistory && (
                            <RecipeVersionPager
                                viewingVersionNumber={state.viewingVersionNumber.get}
                                latestVersionNumber={utils.currentRecipeVersionNumber}
                                onNavFirst={handlers.handleNavFirst}
                                onNavPrev={handlers.handleNavPrev}
                                onNavNext={handlers.handleNavNext}
                                onNavLatest={handlers.handleNavLatest}/>
                        )}
                    </div>
                    <div className={styles.footerRight}>
                        <FlintButton_Cancel disabled={state.isLoading.get} onClick={handlers.handleCancelClicked}/>
                        {!utils.isViewOnlyMode && (
                            <FlintButton_Save disabled={state.isLoading.get}
                                              onClick={handlers.handleSaveRecipeClicked}/>
                        )}
                    </div>
                </div>
            </div>

            <SapCodeEditPopup
                isOpen={state.isReminderSapEditOpen.get}
                initialValue={state.recipe.get?.SapCode ?? undefined}
                onSave={handlers.handleReminderSapSave}
                onClose={handlers.handleReminderSapClose}
            />
        </div>
    )
}

export interface OrderRecipeEditComponentProps {
    OrderGuid: GUID;
    ColourStandardGuid: GUID;
    RecipeGuid?: GUID;
    CanAddComponents?: boolean;
    SeriesId?: number;
    SubstrateId?: number;
    ColourStandardName?: string;
    ColourType?: NameWithId | null;
    SapCodeOverride?: string;
    ReadOnly?: boolean;
    AssignmentGuid?: GUID | null;
    OnCancelClicked?: () => void;
    OnRecipeSaved?: (recipe: OrderRecipePreviewModel) => void;
}

const FULL_SEARCH_STORAGE_KEY = "recipe_component_full_search";

export function useOrderRecipeEditComponent(props: OrderRecipeEditComponentProps) {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const {showNotification} = useFlintNotificationContext();
    const {showConfirmDialog} = useFlintDialogContext();
    const {popups} = useVivoPopupContext();
    const [recipeModule, setRecipeModule] = useState<OrderRecipeEditModuleModel>();
    const isFullSearchInitialisedRef = useRef<boolean>(false);
    const [isFullSearch, setIsFullSearch] = useState<boolean>(() => {
        const stored = localStorage.getItem(FULL_SEARCH_STORAGE_KEY);
        isFullSearchInitialisedRef.current = stored !== null;
        return stored !== null ? stored === "true" : false;
    });
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [isReminderSapEditOpen, setIsReminderSapEditOpen] = useState(false);
    const [changeReason, setChangeReason] = useState<string>("");
    const [changeReasonError, setChangeReasonError] = useState<string | undefined>();

    // Version navigation
    const [viewingVersionNumber, setViewingVersionNumber] = useState<number | null>(null);
    const [viewingVersionDetail, setViewingVersionDetail] = useState<OrderRecipeVersionModel | null>(null);
    const [isLoadingVersion, setIsLoadingVersion] = useState<boolean>(false);
    const versionAbortRef = useRef<AbortController | null>(null);

    const recipeValidationSchema = useMemo(() => Yup.object().shape({
        CodeGeneratorNumber: Yup.string().optional().max(50, t("RECIPES_VALIDATION_CodeGeneratorNumberMax")),
        SapCode: Yup.string().nullable().max(8, t("RECIPES_VALIDATION_SapCodeMax")),
        UnitOfMeasure: Yup.object().shape({
            Id: Yup.number()
                .typeError(t("RECIPES_VALIDATION_UnitOfMeasureRequired"))
                .required(t("RECIPES_VALIDATION_UnitOfMeasureRequired"))
                .min(1, t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
            Name: Yup.string().required(t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
        }).required(t("RECIPES_VALIDATION_UnitOfMeasureRequired")),
        ColourTypeId: Yup.number()
            .typeError(t("RECIPES_VALIDATION_ColourTypeRequired"))
            .required(t("RECIPES_VALIDATION_ColourTypeRequired"))
            .min(1, t("RECIPES_VALIDATION_ColourTypeRequired")),
        Components: Yup.array()
            .min(1, t("RECIPES_VALIDATION_ComponentRequired"))
            .of(
                Yup.object().shape({
                    InkItemName: Yup.string().required(t("RECIPES_VALIDATION_ComponentNameRequired")),
                    InkItemDescription: Yup.string().nullable(),
                    Amount: Yup.number().required(t("RECIPES_VALIDATION_AmountRequired")).min(0.01, t("RECIPES_VALIDATION_AmountPositive")),
                })
            ),
    }), [t]);
    const {
        formErrors: recipeFormErrors,
        validateForm: recipeValidateForm,
        scrollToFirstInvalid
    } = useVivoValidation(recipeValidationSchema);

    useEffect(() => {
        const controller = new AbortController();
        loadData(controller.signal).catch(() => {});
        return () => {
            controller.abort();
            versionAbortRef.current?.abort();
        };
    }, []);

    async function loadData(signal: AbortSignal) {
        setIsLoading(true);
        const params = {
            orderGuid: props.OrderGuid,
            csGuid: props.ColourStandardGuid,
            recipeGuid: props.RecipeGuid,
            assignmentGuid: props.AssignmentGuid ?? undefined,
        };
        try {
            const response = await axios.get<ApiResponseDto<OrderRecipeEditModuleModel>>(ApiUrl_Orders_GetOrderRecipe,
                {params: params, signal: signal});
            const model = response.data.Result;
            if (!model.Recipe.UnitOfMeasure || (model.Recipe.UnitOfMeasure.Id ?? 0) === 0) {
                model.Recipe.UnitOfMeasure = model.UnitOfMeasureList[0];
            }
            if (model.Recipe.Components.length === 0)
                model.Recipe.Components.push(createNewComponent());
            if (!isFullSearchInitialisedRef.current) {
                const defaultFullSearch = !model.ByDefaultSearchComponentsInCurrentSeriesOnly;
                setIsFullSearch(defaultFullSearch);
                isFullSearchInitialisedRef.current = true;
            }
            if (props.SapCodeOverride !== undefined) {
                model.Recipe.SapCode = props.SapCodeOverride;
            }
            setRecipeModule(model);
        } catch (err: any) {
            if (!axios.isCancel(err)) showNotification(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!recipeModule?.Recipe.Components) {
            setTotalAmount(0);
            return;
        }
        setTotalAmount(recipeModule?.Recipe.Components.reduce((acc, c) => acc + c.Amount, 0));
    }, [recipeModule?.Recipe.Components])

    function createNewComponent(): OrderRecipeComponentEditModel {
        return {
            Id: 0,
            Guid: NewGuid(),
            InkItemName: "",
            InkItemDescription: "",
            Amount: 0,
        };
    }

    function handleAddNewComponentClicked() {
        setRecipeModule(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                Recipe: {
                    ...prev.Recipe,
                    Components: [...prev.Recipe.Components, createNewComponent()],
                },
            };
        });
    }

    function handleComponentDeleted(index: number) {
        setRecipeModule(prev => {
            if (!prev) return prev;
            const components = prev.Recipe.Components.filter((_, i) => i !== index);
            return {...prev, Recipe: {...prev.Recipe, Components: components}};
        });
    }

    function handleOnRecipeUpdated<T extends keyof OrderRecipeEditModel>(key: T, value: OrderRecipeEditModel[T]) {
        setRecipeModule(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                Recipe: {
                    ...prev?.Recipe,
                    [key]: value
                }
            }
        });
    }

    async function handleGenerateDescriptionClicked() {
        try {
            const description = await popups.recipeDescriptionGeneratorPopup.start({
                OrderGuid: props.OrderGuid,
                InitialInkSerieId: effectiveSeriesId,
                InitialSubstrateId: effectiveSubstrateId,
                InitialColourStandardName: effectiveColourStandardName,
                InitialColourType: effectiveColourType,
            });
            handleOnRecipeUpdated("Description", description);
        } catch {
            // user cancelled
        }
    }

    function handleOnComponentUpdated<T extends keyof OrderRecipeComponentEditModel>
    (index: number, key: T, value: OrderRecipeComponentEditModel[T]) {
        setRecipeModule(prev => {
            if (!prev) return prev;
            const comps = prev.Recipe.Components;
            if (index < 0 || index >= comps.length) return prev;
            const nextComponents = comps.slice();
            nextComponents[index] = {...nextComponents[index], [key]: value};
            return {...prev, Recipe: {...prev.Recipe, Components: nextComponents}};
        });
    }

    async function validateForm(): Promise<boolean> {
        try {
            await recipeValidateForm(recipeModule?.Recipe);
            return true;
        } catch (error: any) {
            showNotification(error?.messages ?? [], {style: "error"});
            setTimeout(() => scrollToFirstInvalid(), 50);
            return false;
        }
    }

    function MapToOrderRecipeSaveRequest(recipe?: OrderRecipeEditModel): OrderRecipeSaveRequest {
        if (!recipe) throw "Recipe is not loaded!";
        return {
            ...recipe,
            OrderGuid: props.OrderGuid,
            ColorStandardId: recipe.ColourStandardId,
            UnitOfMeasureId: recipe.UnitOfMeasure.Id,
            ColourTypeId: recipe.ColourTypeId,
            Components: recipe.Components.map(c => MapToOrderRecipeComponentSaveRequest(c)),
            ChangeReason: props.RecipeGuid ? (changeReason || null) : null,
        }
    }

    function MapToOrderRecipeComponentSaveRequest(component: OrderRecipeComponentEditModel): OrderRecipeComponentSaveRequest {
        return {
            Id: component.Id,
            Guid: component.Guid,
            InkItemGuid: component.InkItemGuid,
            InkItemName: component.InkItemName,
            InkItemDescription: component.InkItemDescription,
            Amount: component.Amount,
        }
    }

    async function executeSave(recipe: OrderRecipeEditModel) {
        setIsLoading(true);
        try {
            const post = MapToOrderRecipeSaveRequest(recipe);
            const result = await axios.post<ApiResponseDto<OrderRecipePreviewModel>>(ApiUrl_Orders_SaveOrderRecipe, post);
            props.OnRecipeSaved?.(result.data.Result);
        } catch (error: any) {
            showNotification(error);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleSaveRecipeClicked() {
        if (isViewOnlyMode) return;
        if (!await validateForm()) return;
        if (props.RecipeGuid && !changeReason.trim()) {
            setChangeReasonError(t("ORDER_RECIPE_ChangeReasonRequired"));
            return;
        }
        const sapCode = recipeModule?.Recipe?.SapCode;
        if (sapCode !== null && sapCode !== undefined && !sapCode) {
            showConfirmDialog({
                content: t("SAP_CODE_ReminderMessage"),
                acceptButtonText: t("SAP_CODE_ReminderYes"),
                cancelButtonText: t("SAP_CODE_ReminderNo"),
                onAccept: handleReminderYes,
                onCancel: handleReminderNo,
            });
            return;
        }
        await executeSave(recipeModule!.Recipe);
    }

    function handleReminderYes() {
        setIsReminderSapEditOpen(true);
    }

    async function handleReminderNo() {
        await executeSave(recipeModule!.Recipe);
    }

    async function handleReminderSapSave(newSapCode: string) {
        handleOnRecipeUpdated("SapCode", newSapCode);
        await executeSave({...recipeModule!.Recipe, SapCode: newSapCode});
    }

    function handleReminderSapClose() {
        setIsReminderSapEditOpen(false);
    }

    function handleCancelClicked() {
        props.OnCancelClicked?.();
    }

    function handleChangeReasonChanged(value: string) {
        setChangeReason(value);
        setChangeReasonError(undefined);
    }

    function handleFullSearchToggled(value: boolean) {
        setIsFullSearch(value);
        localStorage.setItem(FULL_SEARCH_STORAGE_KEY, String(value));
    }

    // ── Version navigation ────────────────────────────────────────────────────

    async function loadVersionDetail(versionNumber: number) {
        versionAbortRef.current?.abort();
        const controller = new AbortController();
        versionAbortRef.current = controller;
        setIsLoadingVersion(true);
        try {
            const response = await axios.get<ApiResponseDto<OrderRecipeVersionModel>>(
                ApiUrl_OrderRecipe_GetVersion,
                {params: {recipeGuid: props.RecipeGuid, versionNumber}, signal: controller.signal}
            );
            setViewingVersionDetail(response.data.Result);
            setViewingVersionNumber(versionNumber);
        } catch (err: any) {
            if (!axios.isCancel(err)) showNotification(err);
        } finally {
            setIsLoadingVersion(false);
        }
    }

    async function handleNavFirst() {
        await loadVersionDetail(1);
    }

    async function handleNavPrev() {
        const current = viewingVersionNumber !== null ? viewingVersionNumber : currentRecipeVersionNumber;
        if (current > 1) await loadVersionDetail(current - 1);
    }

    async function handleNavNext() {
        if (viewingVersionNumber === null) return;
        const next = viewingVersionNumber + 1;
        if (next >= currentRecipeVersionNumber) {
            setViewingVersionNumber(null);
            setViewingVersionDetail(null);
        } else {
            await loadVersionDetail(next);
        }
    }

    function handleNavLatest() {
        setViewingVersionNumber(null);
        setViewingVersionDetail(null);
    }

    // ── Derived ──────────────────────────────────────────────────────────────

    const isLabDivisionMismatch = props.CanAddComponents === false;
    const effectiveSeriesId = props.SeriesId ?? recipeModule?.OrderItemInkSerie?.Id;
    const effectiveSubstrateId = props.SubstrateId ?? recipeModule?.OrderItemSubstrate?.Id;
    const effectiveColourStandardName = props.ColourStandardName || recipeModule?.ColourStandardName || undefined;
    const effectiveColourType = props.ColourType ?? recipeModule?.ColourType ?? null;
    const colourTypeList = recipeModule?.RecipeColourTypeList ?? [];
    const selectedColourType = colourTypeList.find(x => x.Id === (recipeModule?.Recipe.ColourTypeId ?? 0));
    const currentRecipeVersionNumber = recipeModule?.Recipe.VersionNumber ?? 0;
    const hasHistory = !!props.RecipeGuid && currentRecipeVersionNumber > 1;
    const isHistoricalMode = viewingVersionNumber !== null;
    const isViewOnlyMode = !!props.ReadOnly || isHistoricalMode;
    const historyDisplayRecipe: OrderRecipeEditModel | null = useMemo(() => {
        if (!viewingVersionDetail || !recipeModule) return null;
        const uom = recipeModule.UnitOfMeasureList.find(u => u.Id === viewingVersionDetail.UnitOfMeasureId)
            ?? {Id: 0, Name: ''} as NameWithId;
        return {
            ...recipeModule.Recipe,
            CodeGeneratorNumber: viewingVersionDetail.CodeGeneratorNumber,
            SapCode: viewingVersionDetail.SapCodeSnapshot,
            Description: viewingVersionDetail.Description,
            UnitOfMeasure: uom,
            ColourTypeId: viewingVersionDetail.ColourTypeId ?? 0,
            VersionNumber: viewingVersionDetail.VersionNumber,
            ChangeReason: viewingVersionDetail.ChangeReason,
            Components: viewingVersionDetail.Components.map((c, i) => ({
                Id: 0,
                Guid: (c.SourceComponentGuid ?? `hist-${i}`) as GUID,
                InkItemGuid: c.InkItemGuid,
                InkItemName: c.InkItemName,
                InkItemDescription: c.InkItemDescription,
                Amount: c.Amount,
            })),
        };
    }, [viewingVersionDetail, recipeModule]);

    const displayRecipe = isHistoricalMode ? historyDisplayRecipe : (recipeModule?.Recipe ?? null);
    const displayTotalAmount = isHistoricalMode
        ? (viewingVersionDetail?.Components.reduce((acc, c) => acc + c.Amount, 0) ?? 0)
        : totalAmount;
    const displaySelectedColourType = isHistoricalMode
        ? colourTypeList.find(x => x.Id === viewingVersionDetail?.ColourTypeId)
        : selectedColourType;

    return {
        state: {
            isLoading: {get: isLoading},
            isLoadingVersion: {get: isLoadingVersion},
            recipe: {get: recipeModule?.Recipe},
            totalAmount: {get: totalAmount},
            isFullSearch: {get: isFullSearch},
            isReminderSapEditOpen: {get: isReminderSapEditOpen},
            changeReason: {get: changeReason, error: changeReasonError},
            viewingVersionNumber: {get: viewingVersionNumber},
            viewingVersionDetail: {get: viewingVersionDetail},
        },
        utils: {
            unitsOfMeasure: recipeModule?.UnitOfMeasureList ?? [],
            colourTypeList,
            selectedColourType,
            displayRecipe,
            displayTotalAmount,
            displaySelectedColourType,
            isLabDivisionMismatch,
            hasSeriesContext: (effectiveSeriesId ?? 0) > 0,
            effectiveSeriesId,
            hasHistory,
            isHistoricalMode,
            isViewOnlyMode,
            currentRecipeVersionNumber,
            validators: {
                recipeValidator: {
                    validateForm: recipeValidateForm,
                    formErrors: recipeFormErrors,
                }
            },
        },
        handlers: {
            handleOnRecipeUpdated,
            handleAddNewComponentClicked,
            handleComponentDeleted,
            handleOnComponentUpdated,
            handleSaveRecipeClicked,
            handleCancelClicked,
            handleChangeReasonChanged,
            handleFullSearchToggled,
            handleGenerateDescriptionClicked,
            handleReminderYes,
            handleReminderNo,
            handleReminderSapSave,
            handleReminderSapClose,
            handleNavFirst,
            handleNavPrev,
            handleNavNext,
            handleNavLatest,
        }
    }
}
